// The public demo: the "sample data only" bar across the top (with Reset and the tour), the
// welcome that opens the first time, and the key screen of a private demo. Everything here
// shows only when /api/status says the server is the demo.
import { useEffect, useState } from 'react';
import { ApiError, post } from '../lib/api';
import { navigate, storageGet, storageSet } from '../lib/hooks';
import { DEMO_SEEN as SEEN, demoDoneHere as doneHere, rememberDemoCopy as rememberCopy } from '../lib/demo';
import { useSession, type Status } from '../lib/session';
import { setDemoKind, type DemoKind } from '../lib/demoKind';
import { Button, Field, Sheet, useToast } from '../ui/kit';
import { Icon } from '../ui/icons';
import { BrandLogo } from '../ui/brand';
import { LegalFooter } from '../ui/legal';

interface Step { key: string; label: string; sub: string; to: string; done: boolean }

const KINDS: { id: DemoKind; label: string }[] = [
  { id: 'cellphone', label: 'Phone repair' }, { id: 'computer', label: 'Computer repair' }, { id: 'auto', label: 'Auto repair' }, { id: 'vacuum', label: 'Vacuum repair' },
];

function steps(status: Status): Step[] {
  const p = status.demo_progress ?? { sale: false, repair: false, buy: false, approval: false };
  const here = status.demo_copy ? doneHere(status.demo_copy.id) : {};
  const kind = status.demo_industry ?? 'cellphone';
  if (kind !== 'cellphone') {
    const auto = kind === 'auto';
    const item = kind === 'computer' ? 'computer' : kind === 'vacuum' ? 'vacuum' : 'vehicle';
    return [
      { key: 'repair', label: auto ? 'Write a work order' : `Check in a ${item}`, sub: auto ? 'Tap Work order: the vehicle (typed in), what’s wrong, parts and labor.' : `Tap Repair: the ${item}, its drop-off check, parts and labor.`, to: '/repairs/new', done: p.repair },
      { key: 'approval', label: 'Get the customer’s OK', sub: 'Open one that needs the customer’s OK and record their answer (signed, or by phone, text or email).', to: '/repairs?status=waiting_customer', done: !!p.approval },
      { key: 'sale', label: 'Ring up a sale', sub: auto ? 'Tap Sale: oil, filters, wipers; take cash or card.' : 'Tap Sale: parts and accessories; take cash or card.', to: '/sale', done: p.sale },
      { key: 'receipt', label: auto ? 'Print a work order' : 'Print a ticket', sub: 'Open a ticket and tap Print: the printout shows on screen in the demo.', to: '/repairs?status=all', done: !!here.receipt },
      { key: 'reports', label: 'Look at the numbers', sub: 'Today, this week, parts and labor, what each person did.', to: '/reports', done: !!here.reports },
    ];
  }
  return [
    { key: 'sale', label: 'Ring up a sale', sub: 'Tap Sales, pick a phone or an accessory, take cash or card.', to: '/sale', done: p.sale },
    { key: 'repair', label: 'Check in a repair', sub: 'Tap Repairs → Check in: the model, the price list, the ticket.', to: '/repairs/new', done: p.repair },
    { key: 'buy', label: 'Buy a phone or take a trade-in', sub: 'An instant offer, the IMEI, the seller — no ID needed here.', to: '/buy', done: p.buy },
    { key: 'receipt', label: 'See a receipt or ticket', sub: 'Print buttons show the printout on screen in the demo.', to: '/sales', done: !!here.receipt },
    { key: 'reports', label: 'Look at the numbers', sub: 'Today, this week, profit, what each person sold.', to: '/reports', done: !!here.reports },
  ];
}

function timeLeft(expiresAt: number, now: number): string {
  const m = Math.max(0, Math.round((expiresAt - now) / 60_000));
  if (m >= 60) return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')} min`;
  return `${m} min`;
}

/** The bar across the top of every demo screen. */
export function DemoBar() {
  const { status, refresh } = useSession();
  const toast = useToast();
  const [tour, setTour] = useState(false);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  const copy = status?.demo_copy;
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (copy) rememberCopy(copy.id);
  }, [copy?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // A copy that ran out: the next refresh signs the visitor into a new one.
  useEffect(() => {
    if (!copy) return;
    const left = copy.expires_at - Date.now();
    if (left <= 0) return;
    const t = setTimeout(() => { toast('Your demo copy reached its 2 hours and was reset.', 'info'); location.reload(); }, left + 1_000);
    return () => clearTimeout(t);
  }, [copy?.expires_at]); // eslint-disable-line react-hooks/exhaustive-deps
  // Another kind of sample shop: this tab shows it from now on (its own copy, made on the next
  // request; a copy this visitor already has of that kind comes back as they left it). Links from
  // the website (…/?industry=auto) are read before the first request (lib/demoKind).
  const switchKind = (industry: DemoKind) => {
    if (industry === (status?.demo_industry ?? 'cellphone')) return;
    setBusy(true);
    setDemoKind(industry);
    storageSet(SEEN, '');
    location.hash = '#/';
    location.reload();
  };
  if (!status?.demo_site) return null;
  const reset = async () => {
    if (!confirm('Start the demo over? Everything you did in this copy goes away and the sample shop comes back as it was.')) return;
    setBusy(true);
    try {
      await post('/api/demo/reset');
      storageSet(SEEN, '');
      location.hash = '#/';
      location.reload();
    } catch (e) {
      setBusy(false);
      toast(e instanceof ApiError ? e.message : 'Couldn’t reset the demo.', 'error');
    }
  };
  const done = status.demo_copy ? steps(status).filter((s) => s.done).length : 0;
  return (
    <>
      <div className="demo-bar" role="region" aria-label="Demo">
        <span className="demo-dot" aria-hidden="true" />
        <span className="demo-text">
          <strong>Demo</strong> — sample data only. {status.demo_shop ? `${status.demo_shop} is made up; ` : ''}nothing here texts, calls, charges or prints.
          {copy ? <span className="demo-left"> Your copy resets in {timeLeft(copy.expires_at, now)}.</span> : null}
        </span>
        <span className="demo-acts">
          {copy ? (
            <select className="demo-kind" aria-label="Kind of sample shop" value={status.demo_industry ?? 'cellphone'} disabled={busy} onChange={(e) => switchKind(e.target.value as DemoKind)}>
              {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
          ) : null}
          {copy ? <button type="button" className="demo-btn ghost" onClick={() => setTour(true)}><Icon.star /> Tour {done ? `${done}/5` : ''}</button> : null}
          {status.demo_signup_url ? <a className="demo-btn cta" href={status.demo_signup_url} target="_blank" rel="noopener">Get STORE OS</a> : null}
          {copy ? <button type="button" className="demo-btn" onClick={reset} disabled={busy}><Icon.refresh /> Reset</button> : null}
        </span>
      </div>
      {tour && copy ? <DemoTour onClose={() => setTour(false)} onRefresh={refresh} /> : null}
      {copy ? <DemoWelcome key={copy.id} /> : null}
    </>
  );
}

/** The first thing a visitor sees in each new copy: who they are, what to try. */
function DemoWelcome() {
  const { status } = useSession();
  const copyId = status?.demo_copy?.id ?? '';
  const [open, setOpen] = useState(() => storageGet<string>(SEEN, '') !== copyId);
  if (!open || !status?.demo_copy) return null;
  const close = () => { storageSet(SEEN, copyId); setOpen(false); };
  const shop = status.demo_shop ?? 'the sample shop';
  const owner = status.demo_people?.[0];
  return (
    <Sheet title={`Welcome to ${shop}`} onClose={close} focusFirst={false} footer={<Button variant="primary" size="lg" onClick={close}>Start exploring</Button>}>
      <div className="demo-welcome">
        <BrandLogo className="demo-welcome-logo" animate />
        <p className="muted">
          This is a private copy of STORE OS running a made-up {KINDS.find((k) => k.id === (status.demo_industry ?? 'cellphone'))?.label.toLowerCase() ?? 'repair'} shop. You’re signed in as <strong>{owner?.name ?? 'the owner'}</strong>, the owner, at the
          counter with the drawer open. Sell, fix, print, change anything: it’s yours for about two hours, then it resets. Pick another kind of shop at the top any time.
        </p>
        <ul className="demo-steps">
          {steps(status).map((s) => (
            <li key={s.key}><Icon.chevronRight /><span><strong>{s.label}.</strong> {s.sub}</span></li>
          ))}
        </ul>
        <p className="small muted">Texting, calls, card readers, printers, IMEI checks and email are switched off here, so nothing can go out by mistake. Print buttons show the printout on the screen instead.</p>
      </div>
    </Sheet>
  );
}

/** The tour: the five things to try, ticked off as they're done, with the team's clock-in codes. */
function DemoTour({ onClose, onRefresh }: { onClose: () => void; onRefresh: () => Promise<void> }) {
  const { status } = useSession();
  useEffect(() => { onRefresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!status) return null;
  const list = steps(status);
  const go = (to: string) => { onClose(); navigate(to); };
  return (
    <Sheet title="Things to try" onClose={onClose} focusFirst={false} footer={<Button variant="primary" onClick={onClose}>Done</Button>}>
      <ol className="demo-tour">
        {list.map((s, i) => (
          <li key={s.key} className={s.done ? 'done' : ''}>
            <span className="demo-tour-n">{s.done ? <Icon.check /> : i + 1}</span>
            <span className="grow">
              <span className="strong" style={{ display: 'block' }}>{s.label}</span>
              <span className="small muted" style={{ display: 'block' }}>{s.sub}</span>
            </span>
            <Button size="sm" onClick={() => go(s.to)}>{s.done ? 'Again' : 'Go'}</Button>
          </li>
        ))}
      </ol>
      <h3 style={{ marginTop: 20 }}>The team</h3>
      <p className="small muted">Tap <strong>Lock</strong> to see the clock-in screen and sign in as someone else. Everyone’s code is shown here (only in the demo).</p>
      <table className="table demo-people">
        <tbody>
          {status.demo_people?.map((p) => (
            <tr key={p.name}><td className="strong">{p.name}</td><td className="muted">{p.role}{p.store ? ` · ${p.store}` : ''}</td><td className="num">code {p.pin}</td></tr>
          ))}
        </tbody>
      </table>
      {status.demo_people?.[0]?.email ? <p className="small muted">Email sign-in (a computer in the back office): {status.demo_people[0].email} / {status.demo_people[0].password}</p> : null}
      {status.demo_signup_url ? <p><a className="link-btn" href={status.demo_signup_url} target="_blank" rel="noopener">Get STORE OS for your shop →</a></p> : null}
    </Sheet>
  );
}

/** A private demo: the access key from the owner opens it in this browser. */
export function DemoKeyScreen() {
  const { refresh, status } = useSession();
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post('/api/demo/unlock', { key });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t open the demo.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <main className="gate">
        <form className="gate-card" onSubmit={submit}>
          <div className="gate-brand">
            <BrandLogo className="gate-wcw" animate />
            <div className="gate-os"><img src="/assets/store-os-icon-128.png" alt="" width={20} height={20} /><span className="strong">STORE OS</span><span>Demo</span></div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <h1>This demo is private</h1>
            <p className="muted">Enter the access key to open the {status?.demo_shop ?? 'sample shop'} demo.</p>
          </div>
          <Field label="Access key"><input className="input input-lg" type="password" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="off" autoFocus required /></Field>
          {error ? <div className="error-text" role="alert">{error}</div> : null}
          <Button type="submit" variant="primary" size="lg" block busy={busy}>Open the demo</Button>
        </form>
      </main>
      <LegalFooter />
    </>
  );
}
